/* REPLACE ENTIRE FILE WITH THIS VERSION */
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import "../components/QuotesPage.css";

/* ---------------- helpers ---------------- */
const money = (n, currency = "EUR") => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "€ 0.00";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(v);
  } catch {
    return `€ ${v.toFixed(2)}`;
  }
};

function formatDate(value) {
  if (!value) return "-";
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return `${d}-${m}-${y}`;
  }
  const dt = new Date(s);
  if (!isNaN(dt)) {
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yyyy = dt.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  return s;
}

function StatusBadge({ paid }) {
  const bg = paid
    ? "var(--badge-accepted-bg, #dcfce7)"
    : "var(--badge-draft-bg, #f3f4f6)";
  const fg = paid
    ? "var(--badge-accepted-fg, #15803d)"
    : "var(--badge-draft-fg, #374151)";
  const br = paid
    ? "var(--badge-accepted-br, #bbf7d0)"
    : "var(--badge-draft-br, #e5e7eb)";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 8px",
        borderRadius: 999,
        fontSize: 12,
        background: bg,
        color: fg,
        border: `1px solid ${br}`,
      }}
    >
      {paid ? "paid" : "unpaid"}
    </span>
  );
}

// --- Email templates (backend-first, localStorage fallback) ---
const LS_KEYS = { invoice: "jwcs.emailTemplate.invoice" };
const DEFAULTS = {
  invoice: `Hello,

Please find attached your invoice {{number}}.

Kind regards,`,
};

async function loadEmailTemplate(type) {
  try {
    const res = await fetch(`/api/email-templates?type=${encodeURIComponent(type)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return (json && json.message) || DEFAULTS[type];
  } catch {
    return localStorage.getItem(LS_KEYS[type]) || DEFAULTS[type];
  }
}

function mergePlaceholders(template, ctx) {
  return String(template || "")
    .replace(/\{\{number\}\}/g, ctx.number || "")
    .replace(/\{\{customer\}\}/g, ctx.customer || "")
    .replace(/\{\{company\}\}/g, ctx.company || "");
}

/* ---------------- component ---------------- */
export default function InvoicesPage() {
  const navigate = useNavigate();

  // filters / paging
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // selection
  const [selected, setSelected] = useState(() => new Set());
  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const selectedCount = selectedIds.length;
  const canSingleAct = selectedCount === 1;

  // pdf modal
  const [open, setOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(undefined);
  const [pdfName, setPdfName] = useState("invoice.pdf");
  const [numPages, setNumPages] = useState(null);
  const [pdfErr, setPdfErr] = useState("");

  // email
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailMsg, setEmailMsg] = useState("");

  // delete confirm
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmRow, setConfirmRow] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const confirmNoRef = useRef(null);

  // responsive PDF width
  const pdfContainerRef = useRef(null);
  const [pdfWidth, setPdfWidth] = useState(900);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / pageSize)),
    [total, pageSize]
  );

  const fetchInvoices = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status && status !== "all") params.set("status", status);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));

    setLoading(true);
    setErr("");
    try {
      const res = await fetch(`/api/invoices?${params.toString()}`);
      if (!res.ok)
        throw new Error(
          (await res.text().catch(() => "")) || `HTTP ${res.status}`
        );
      const json = await res.json();
      const mapped = (json.data || []).map((r) => ({
        id: r.id,
        number: r.invoice_number,
        customer:
          `${r.first_name || ""} ${r.surname || ""}`.trim() || "-",
        date: r.created_at,
        paid: Number(r.paid) === 1,
        total: Number(r.total),
        pdfUrl: `/api/invoices/${r.id}/pdf`,
      }));
      setRows(mapped);
      setTotal(Number(json.total || mapped.length));
      // trim selection to visible
      setSelected((prev) => {
        const next = new Set(prev);
        const visible = new Set(mapped.map((r) => r.id));
        for (const id of Array.from(next))
          if (!visible.has(id)) next.delete(id);
        return next;
      });
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [q, status, from, to, page, pageSize]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // resize PDF pages to fit modal width
  useEffect(() => {
    if (!open) return;

    function updateWidth() {
      if (pdfContainerRef.current) {
        const w = pdfContainerRef.current.clientWidth;
        setPdfWidth(w > 0 ? w : 900);
      }
    }

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [open]);

  function onPdfLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  async function openPdf(url, name) {
    if (!url) return;
    setPdfErr("");
    setNumPages(null);
    setPdfName(name || "invoice.pdf");
    setOpen(true);

    if (pdfUrl && String(pdfUrl).startsWith("blob:")) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(undefined);
    }

    try {
      const res = await fetch(url);
      if (!res.ok)
        throw new Error(
          (await res.text().catch(() => "")) || `HTTP ${res.status}`
        );
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      setPdfUrl(objUrl);
    } catch (e) {
      setPdfErr(String(e.message || e));
    }
  }

  function downloadCurrentPdf() {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = pdfName || "invoice.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async function togglePaid(row) {
    try {
      const res = await fetch(`/api/invoices/${row.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paid: row.paid ? 0 : 1 }),
      });
      if (!res.ok)
        throw new Error(
          (await res.text().catch(() => "")) || `HTTP ${res.status}`
        );
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id ? { ...r, paid: !row.paid } : r
        )
      );
    } catch (e) {
      alert(`Failed to update: ${e.message || e}`);
    }
  }

  // ----- EMAIL: now pulls template & merges placeholders -----
  async function emailInvoice(row) {
    if (!row) return;
    setCurrentInvoice(row);
    setEmailSending(true);
    setEmailMsg("");
    try {
      const customer = row.customer || "";
      const company = document
        ?.querySelector('meta[name="app-company"]')
        ?.getAttribute("content") || ""; // optional

      const template = await loadEmailTemplate("invoice");
      const message = mergePlaceholders(template, {
        number: row.number,
        customer,
        company,
      });

      const res = await fetch(`/api/invoices/${row.id}/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: `Invoice ${row.number}`,
          message,
        }),
      });
      if (!res.ok)
        throw new Error(
          (await res.text().catch(() => "")) || `HTTP ${res.status}`
        );
      setEmailMsg("Email sent ✓");
      setTimeout(() => setEmailMsg(""), 4000);
    } catch (e) {
      setEmailMsg(`Failed: ${e.message || e}`);
    } finally {
      setEmailSending(false);
    }
  }

  // confirm delete
  function requestDelete(row) {
    setConfirmRow(row);
    setConfirmOpen(true);
    setTimeout(() => confirmNoRef.current?.focus(), 0);
  }
async function deleteInvoice(row) {
  if (!row?.id) return;

  setDeletingId(row.id);

  // Try several possible backend endpoints/methods in sequence.
  const attempts = [
    { url: `/api/invoices/${row.id}`, options: { method: "DELETE" } },
    { url: `/api/invoices/delete?id=${encodeURIComponent(row.id)}`, options: { method: "POST" } },
    { url: `/api/invoices/${row.id}/delete`, options: { method: "POST" } },
    { url: `/api/invoices/delete/${row.id}`, options: { method: "POST" } },
  ];

  let lastErrText = "";
  let success = false;

  for (const a of attempts) {
    try {
      const res = await fetch(a.url, a.options);
      if (res.ok) {
        success = true;
        break;
      } else {
        lastErrText =
          (await res.text().catch(() => "")) || `HTTP ${res.status}`;
        // If it's a 404/405, try next attempt
        if (res.status === 404 || res.status === 405) continue;
        // For other statuses, bail out fast
        break;
      }
    } catch (e) {
      lastErrText = e?.message || String(e);
      // Try the next attempt
      continue;
    }
  }

  if (!success) {
    alert(`Failed to delete invoice: ${lastErrText || "No delete endpoint available"}`);
    setDeletingId(null);
    setConfirmOpen(false);
    setConfirmRow(null);
    return;
  }

  // Success: update UI
  setRows((prev) => prev.filter((r) => r.id !== row.id));
  setTotal((t) => Math.max(0, (t || 0) - 1));
  setDeletingId(null);
  setConfirmOpen(false);
  setConfirmRow(null);

  // If we just removed the last row on this page, step back a page
  setTimeout(() => {
    if (rows.length === 1 && page > 1) setPage((p) => p - 1);
  }, 0);
}


  // selection helpers
  const allOnPageSelected =
    rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someOnPageSelected =
    rows.some((r) => selected.has(r.id)) && !allOnPageSelected;

  function toggleSelect(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // top actions
  function pickOne() {
    if (!canSingleAct) return null;
    const id = selectedIds[0];
    return rows.find((r) => r.id === id) || null;
  }

  function handleTopView() {
    const row = pickOne();
    if (row) {
      setCurrentInvoice(row);
      openPdf(row.pdfUrl, `invoice-${row.number}.pdf`);
    }
  }
  function handleTopPdf() {
    const row = pickOne();
    if (row?.pdfUrl)
      window.open(row.pdfUrl, "_blank", "noopener,noreferrer");
  }
  function handleTopEmail() {
    const row = pickOne();
    if (row) emailInvoice(row);
  }
  function handleTopTogglePaid() {
    const row = pickOne();
    if (row) togglePaid(row);
  }
  function handleTopDelete() {
    const row = pickOne();
    if (row) requestDelete(row);
  }

  return (
    <div className="card">
      {/* (rest of file unchanged other than email) */}
      {/* Layout + table + column widths + pagination */}
      <style>{`
        .inv-topbar {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          align-items: center;
          margin-bottom: 12px;
        }
        .inv-table { width: 100%; border-collapse: collapse; }
        .inv-table th, .inv-table td {
          padding: 8px 10px;
          border-bottom: 1px solid var(--border);
        }
        .inv-table thead th { text-align: left; }
        .inv-table thead th.inv-col-total,
        .inv-table thead th.inv-col-status,
        .inv-table tbody td.inv-col-total,
        .inv-table tbody td.inv-col-status { text-align: right; }
        .inv-col-select{ width:44px } .inv-col-number{ width:160px }
        .inv-col-customer{} .inv-col-date{ width:160px }
        .inv-col-total{ width:140px } .inv-col-status{ width:120px }
        .inv-pagination{
          display:flex;justify-content:space-between;align-items:center;gap:8px;margin-top:10px;flex-wrap:nowrap;
        }
        .inv-pagination__controls{ display:flex;flex-wrap:nowrap;align-items:center;justify-content:center;gap:6px; }
        .inv-pagination__controls .btn{ padding:4px 14px;width:auto;flex:0 0 auto;white-space:nowrap; }
        .inv-pagination__page{ min-width:auto;font-size:12px; }
        .muted{ opacity:.8 }
        .inv-filters{ margin:12px 0;border:1px solid var(--border);background:var(--card);border-radius:12px; }
        .inv-filters__header{ padding:10px 12px;border-bottom:1px solid var(--border);font-weight:600; }
        .inv-filters__body{ padding:12px; }
        .inv-grid{ display:grid;gap:10px;grid-template-columns:repeat(auto-fit, minmax(220px, 1fr)); }
        @media (max-width:719.98px){
          .inv-table thead th{ font-size:11px;padding:6px 4px;white-space:nowrap; }
          .inv-col-select,.inv-col-number,.inv-col-date,.inv-col-total,.inv-col-status{ width:auto; }
          .inv-grid{ grid-template-columns:1fr; }
          .inv-filters .input[type="date"]{ width:100%;max-width:100%;box-sizing:border-box; }
          .inv-col-date{ width:70px;max-width:70px;white-space:nowrap; }
          .inv-col-status{ min-width:90px; }
          .inv-table{ display:table; } .inv-table thead{ display:table-header-group; }
          .inv-table tbody{ display:table-row-group; } .inv-table tr{ display:table-row; }
          .inv-table th,.inv-table td{ display:table-cell; }
          .inv-table td::before{ content:none !important; display:none !important; }
          .inv-pagination{ flex-direction:column;align-items:stretch;gap:4px; }
          .inv-pagination__controls{ display:flex;flex-wrap:nowrap;align-items:center;justify-content:center;gap:6px; }
          .inv-pagination__controls .btn{ padding:4px 14px;width:auto;flex:0 0 auto;white-space:nowrap; }
          .inv-pagination__page{ min-width:auto;font-size:12px; }
        }
      `}</style>

      {/* Header */}
      <h2>Invoices</h2>

      {/* Error banner */}
      {err && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            borderRadius: 8,
            border: "1px solid #fecaca",
            background: "#fee2e2",
            color: "#991b1b",
          }}
        >
          {err}
        </div>
      )}

      {/* Top action bar */}
      <style>{`
        .inv-actions{ display:flex;flex-wrap:wrap;align-items:center;gap:12px; }
        .inv-actions .btn,.inv-actions .qp-btn{ margin:0 }
        .inv-actions .muted{ margin-left:auto }
        @media (max-width:719.98px){
          .inv-actions{ display:grid;grid-template-columns:1fr 1fr;gap:10px;align-items:stretch; }
          .inv-actions .btn,.inv-actions .qp-btn{ width:100%;justify-self:stretch; }
          .inv-actions .btn:nth-of-type(7),.inv-actions .qp-btn:nth-of-type(7){ grid-column:1 / -1;justify-self:start;width:100%; }
          .inv-actions .muted{ grid-column:1 / -1;justify-self:end;margin-left:0; }
        }
      `}</style>

      <div className="inv-actions">
        <button className="btn" onClick={handleTopView} disabled={!canSingleAct}>View</button>
        <button className="btn" onClick={handleTopPdf} disabled={!canSingleAct}>PDF</button>
        <button
          className="btn"
          onClick={handleTopEmail}
          disabled={!canSingleAct || (emailSending && currentInvoice?.id === selectedIds[0])}
          title={canSingleAct ? `Email ${rows.find((r)=>r.id===selectedIds[0])?.number || ""}` : "Select exactly one invoice"}
        >
          {emailSending && canSingleAct && currentInvoice?.id === selectedIds[0] ? "Sending…" : "Email"}
        </button>
        <button className="btn" onClick={() => navigate("/calendar")}>Calendar</button>
        <button className="btn" onClick={handleTopTogglePaid} disabled={!canSingleAct}>Mark Paid / Unpaid</button>
        <button
          className="btn"
          onClick={handleTopDelete}
          disabled={!canSingleAct || deletingId === selectedIds[0]}
          style={{ background:"#991b1b", border:"1px solid #991b1b", color:"#fee2e2" }}
          title={canSingleAct ? "Delete selected invoice" : "Select exactly one invoice"}
        >
          {deletingId === selectedIds[0] ? "Deleting…" : "Delete"}
        </button>
        <button className="btn secondary" onClick={() => navigate("/quotes")}>Back to Quotes</button>

        <div className="muted" style={{ marginLeft: "auto" }}>
          {selectedCount > 0 ? `${selectedCount} selected` : "No invoices selected"}
        </div>
      </div>

      {/* Filters */}
      <div className="inv-filters">
        <div className="inv-filters__header">Filters</div>
        <div className="inv-filters__body">
          <div className="inv-grid">
            <div>
              <label className="label">Search (number or customer)</label>
              <input
                className="input"
                placeholder="e.g. I10000013 or Jane Doe"
                value={q}
                onChange={(e) => { setPage(1); setQ(e.target.value); }}
              />
            </div>
            <div>
              <label className="label">Status</label>
              <select
                className="input"
                value={status}
                onChange={(e) => { setPage(1); setStatus(e.target.value); }}
              >
                <option value="all">All</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>
            <div>
              <label className="label">From</label>
              <input className="input" type="date" value={from} onChange={(e)=>{ setPage(1); setFrom(e.target.value); }} />
            </div>
            <div>
              <label className="label">To</label>
              <input className="input" type="date" value={to} onChange={(e)=>{ setPage(1); setTo(e.target.value); }} />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <table className="inv-table table">
        <thead>
          <tr>
            <th className="inv-col-select">
              <input
                type="checkbox"
                aria-label="Select all on page"
                checked={rows.length>0 && rows.every((r)=>selected.has(r.id))}
                ref={(el) => { if (el) el.indeterminate = rows.some((r)=>selected.has(r.id)) && !rows.every((r)=>selected.has(r.id)); }}
                onChange={(e) => {
                  setSelected((prev) => {
                    const next = new Set(prev);
                    for (const r of rows) e.target.checked ? next.add(r.id) : next.delete(r.id);
                    return next;
                  });
                }}
              />
            </th>
            <th className="inv-col-number">Invoice #</th>
            <th className="inv-col-customer">Customer</th>
            <th className="inv-col-date">Date</th>
            <th className="inv-col-total">Total</th>
            <th className="inv-col-status">Status</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={6} className="muted" style={{ padding: 16, textAlign: "center" }}>Loading…</td>
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={6} className="muted" style={{ padding: 16, textAlign: "center" }}>No invoices found.</td>
            </tr>
          )}
          {!loading && rows.map((r) => (
            <tr key={r.id}>
              <td className="inv-col-select">
                <input
                  type="checkbox"
                  aria-label={`Select invoice ${r.number}`}
                  checked={selected.has(r.id)}
                  onChange={() => setSelected((prev)=>{
                    const next=new Set(prev);
                    if(next.has(r.id)) next.delete(r.id); else next.add(r.id);
                    return next;
                  })}
                />
              </td>
              <td className="inv-col-number" style={{ fontWeight: 600 }}>#{r.number}</td>
              <td className="inv-col-customer">{r.customer || "-"}</td>
              <td className="inv-col-date">{formatDate(r.date)}</td>
              <td className="inv-col-total">{money(r.total)}</td>
              <td className="inv-col-status"><StatusBadge paid={r.paid} /></td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="inv-pagination">
        <div className="muted inv-pagination__summary">
          {(total || 0).toLocaleString()} result{total === 1 ? "" : "s"}
        </div>
        <div className="inv-pagination__controls">
          <button className="btn secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
          <div className="muted inv-pagination__page">Page {page} / {totalPages}</div>
          <button className="btn secondary" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
        </div>
      </div>

      {/* PDF Modal */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 16, zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(1000px, 96vw)", maxHeight: "90vh",
              background: "var(--card)", color: "var(--text)",
              border: "1px solid var(--border)", borderRadius: 12,
              boxShadow: "var(--shadow)", display: "flex",
              flexDirection: "column", overflow: "hidden",
            }}
          >
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", fontWeight: 600 }}>
              Invoice PDF Preview
            </div>

            <div ref={pdfContainerRef} style={{ padding: 16, overflowY: "auto", overflowX: "hidden" }}>
              {pdfErr ? (
                <div style={{ padding: 16, borderRadius: 8, border: "1px solid #fecaca", background: "#fee2e2", color: "#991b1b" }}>
                  Failed to load PDF: {pdfErr}
                </div>
              ) : !pdfUrl ? (
                <div className="muted" style={{ padding: 24, textAlign: "center" }}>Loading PDF…</div>
              ) : (
                <Document
                  file={pdfUrl}
                  onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                  onLoadError={(e) => setPdfErr(String(e?.message || e))}
                  onSourceError={(e) => setPdfErr(String(e?.message || e))}
                  loading={<div className="muted" style={{ padding: 24, textAlign: "center" }}>Loading PDF…</div>}
                >
                  {Array.from(new Array(numPages || 0), (_, idx) => (
                    <Page key={`p_${idx + 1}`} pageNumber={idx + 1} width={pdfWidth} />
                  ))}
                </Document>
              )}
            </div>

            <div
              style={{
                padding: 12, borderTop: "1px solid var(--border)",
                display: "flex", gap: 8, justifyContent: "flex-end",
                alignItems: "center", flexWrap: "wrap",
              }}
            >
              {pdfUrl && (
                <>
                  <button className="btn" onClick={downloadCurrentPdf}>Download</button>
                  <button
                    className="btn"
                    onClick={() => currentInvoice && emailInvoice(currentInvoice)}
                    disabled={emailSending || !currentInvoice}
                    title={currentInvoice ? `Email ${currentInvoice.number}` : "No invoice selected"}
                  >
                    {emailSending ? "Sending…" : "Email to Customer"}
                  </button>
                  {emailMsg && (
                    <span
                      style={{
                        fontSize: 12,
                        color: emailMsg.startsWith("Email sent") ? "#15803d" : "#b91c1c",
                        marginRight: "auto",
                      }}
                    >
                      {emailMsg}
                    </span>
                  )}
                </>
              )}
              <button className="btn secondary" onClick={() => setOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      {confirmOpen && (
        <div
          onClick={() => !deletingId && setConfirmOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 1100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            style={{
              width: "min(520px, 96vw)",
              background: "var(--card)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              borderRadius: 12,
              boxShadow: "var(--shadow)",
            }}
          >
            <div
              id="confirm-title"
              style={{
                padding: "14px 18px",
                borderBottom: "1px solid var(--border)",
                fontWeight: 700,
              }}
            >
              Confirm delete
            </div>
            <div style={{ padding: 18 }}>
              {confirmRow ? (
                <div>
                  Delete invoice <strong>#{confirmRow.number}</strong> (ID {confirmRow.id})? This cannot be undone.
                </div>
              ) : (
                <div>Delete this invoice? This cannot be undone.</div>
              )}
            </div>
            <div
              style={{
                padding: 12,
                borderTop: "1px solid var(--border)",
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
              }}
            >
              <button
                ref={confirmNoRef}
                className="btn secondary"
                onClick={() => setConfirmOpen(false)}
                disabled={!!deletingId}
              >
                No
              </button>
              <button
                className="btn"
                onClick={() => confirmRow && deleteInvoice(confirmRow)}
                disabled={!!deletingId}
                style={{ background: "#fee2e2", border: "1px solid #fecaca", color: "#991b1b" }}
              >
                {deletingId ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email feedback line */}
      {emailMsg && (
        <div style={{ marginTop: 10, fontSize: 12, color: emailMsg.startsWith("Email sent") ? "#15803d" : "#b91c1c" }}>
          {emailMsg}
        </div>
      )}
    </div>
  );
}
